import React, { useEffect, useMemo, useState } from "react";

import {
    Box,
    Button,
    FormHelperText,
    MenuItem,
    Stack,
    TextField,
} from "@mui/material";

import { useDispatch, useSelector } from "react-redux";
import { ClipLoader } from "react-spinners";

import {
    createSourceAsync,
    editSource,
} from "helpers/leads/stepHelpers/sendStep1ToServis";

import { GenericModal } from "components/GenericModal";
import DynamicFieldTable from "components/organisms/DynamicFieldTable";
import SwalAlertComp from "components/SwalAlert";

import { getBaseSource } from "../../../redux/slices/leadSources/leadSources.asyncActions";
import { lastAddedWizzardSource } from "../../../redux/slices/leadsWizardSourceDestination/leadsWizardSourceDestination.slice";

const normalizeDomain = (value) => {
    const trimmed = (value ?? "").trim();
    if (!trimmed) return trimmed;
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const AddEditSourceApi = ({ data, handleCloseSourceApiModal, onSuccess }) => {
    const isEditMode = !!data;

    const dispatch = useDispatch();

    const [id, setID] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [show, setShow] = useState(true);
    const [domain, setDomain] = useState("");
    const [name, setName] = useState("");
    const [baseSource, setBaseSource] = useState("");
    const [error, setError] = useState({});
    const [fieldList, setFieldList] = useState([
        { name: "", type: "", required: false },
    ]);
    const [isEditModeReady, setIsEditModeReady] = useState(false);

    useEffect(() => {
        if (data) {
            setDomain(data?.domain || "");
            setName(data?.name);
            data?.fields?.length &&
                setFieldList(
                    data?.fields?.map((field) => ({
                        ...(field?.id ? { id: field.id } : {}),
                        name: field?.name ?? "",
                        type: field?.type ?? "",
                        required: !!field?.required,
                    })),
                );
            setBaseSource(data?.lead_module_source_api_type_id);
            setID(data?.id);
            setIsEditModeReady(true);
        }
    }, [data]);

    const language = useSelector((state) => state.languagesState.languages);
    const baseSourceTypes = useSelector(
        (state) => state.leadAllSourcesState.sourceApiTypes.data?.source_types,
    );
    const fieldTypes = useSelector(
        (state) => state.leadAllSourcesState.sourceApiTypes.data?.field_types,
    );

    useEffect(() => {
        dispatch(getBaseSource());
    }, [dispatch]);

    const handleClose = () => {
        setShow(false);
        handleCloseSourceApiModal();
    };
    const handleClickCreateBtn = () => {
        const error = validate();
        setError(error);
        if (Object.keys(error).length === 0) {
            const isWebsite = baseSource === "1" || baseSource === 1;
            const normalizedDomain = normalizeDomain(domain);
            if (isWebsite && normalizedDomain !== domain) {
                setDomain(normalizedDomain);
            }
            const createData = {
                name: name,
                source_type_id: baseSource,
                // Only include domain if baseSource is "1" (website)
                ...(isWebsite && normalizedDomain
                    ? { domain: normalizedDomain }
                    : {}),
                fields: fieldList.map((item) => ({
                    ...(item?.id ? { id: item.id } : {}),
                    name: item.name,
                    is_required: !!item.required,
                    type: item.type || null,
                })),
            };
            !isEditMode && createFunc(createData);
            isEditMode && editFunc(createData);
        }
    };
    const editFunc = async (data) => {
        setIsLoading(true);
        let response = await editSource(data, id);
        response.status && onSuccess();
        setIsLoading(false);
    };
    const createFunc = async (data) => {
        setIsLoading(true);

        let response = await createSourceAsync(data);
        if (response.status) {
            dispatch(lastAddedWizzardSource(response.data ?? ""));
            onSuccess();
            handleCloseSourceApiModal();
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response.message && response.message,
                icon: "error",
                timer: 3000,
            });
        }
        setIsLoading(false);
    };
    const validate = () => {
        const error = {};
        if (baseSource === "") error.baseSource = true;
        switch (baseSource) {
            case "1":
                if (domain === "") error.domain = true;
                break;
        }
        if (name === "") error.name = true;

        const hasInvalidField =
            fieldList.length === 0 ||
            fieldList.some((item) => !item.name?.trim() || !item.type);
        if (hasInvalidField) error.fields = true;

        return error;
    };

    const typeOptions = useMemo(
        () =>
            Object.entries(fieldTypes || {}).map(([value, label]) => ({
                value,
                label,
            })),
        [fieldTypes],
    );

    const tableFields = useMemo(
        () =>
            fieldList.map((row) => ({
                ...(row.id ? { id: row.id } : {}),
                name: { value: row.name ?? "" },
                type: { value: row.type ?? "", options: typeOptions },
                required: {
                    value: !!row.required,
                },
            })),
        [fieldList, typeOptions],
    );

    const fieldsConfiguration = [
        {
            name: "name",
            label: language?.lead?.field ?? "Field",
            variant: "TEXTBOX",
            placeholder: language?.lead?.field ?? "Field",
        },
        {
            name: "type",
            label: language?.sources_page?.type ?? "Type",
            variant: "SELECT",
            placeholder: language?.lead?.select_type ?? "Select Type",
        },
        {
            name: "required",
            label: language?.lead?.required ?? "Required",
            variant: "SWITCH",
            defaultValue: false,
            width: "120px",
        },
    ];

    const handleFieldsChange = (newFields) => {
        setFieldList(
            (newFields ?? []).map((row) => ({
                ...(row.id ? { id: row.id } : {}),
                name: row.name?.value ?? "",
                type: row.type?.value ?? "",
                required: !!row.required?.value,
            })),
        );
        if (error.fields) {
            setError((prev) => ({ ...prev, fields: false }));
        }
    };
    const requiredMessage =
        language?.footer?.this_field_is_required ?? "This field is required";

    const modalContent = (
        <Box sx={{ p: 3 }}>
            <Stack spacing={2.5}>
                <TextField
                    fullWidth
                    size="medium"
                    id="name"
                    label={language?.trackers_page?.name ?? "Name"}
                    placeholder="Name"
                    value={name}
                    onChange={(event) => {
                        setName(event.target.value);
                        if (error.name) {
                            setError((prev) => ({ ...prev, name: false }));
                        }
                    }}
                    error={!!error.name}
                    helperText={error.name ? requiredMessage : ""}
                />

                <TextField
                    select
                    fullWidth
                    size="medium"
                    id="authentication-type"
                    label={language?.sources_page?.type ?? "Type"}
                    value={baseSource?.toString() ?? ""}
                    onChange={(event) => {
                        const newBaseSource = event.target.value;
                        setBaseSource(newBaseSource);

                        // Clear baseSource error when type is selected
                        if (error.baseSource) {
                            setError((prev) => ({
                                ...prev,
                                baseSource: false,
                            }));
                        }

                        // Clear domain state and error when type is changed away from "1" (website)
                        if (newBaseSource !== "1") {
                            setDomain("");
                            if (error.domain) {
                                setError((prev) => ({
                                    ...prev,
                                    domain: false,
                                }));
                            }
                        }

                        // Clear field errors when type is selected
                        if (newBaseSource && error.fields) {
                            setError((prev) => ({ ...prev, fields: "" }));
                        }
                    }}
                    error={!!error.baseSource}
                    helperText={error.baseSource ? requiredMessage : ""}
                >
                    {baseSourceTypes &&
                        baseSourceTypes.length > 0 &&
                        baseSourceTypes.map((item, index) => (
                            <MenuItem key={index} value={item.id?.toString()}>
                                {item.name}
                            </MenuItem>
                        ))}
                </TextField>

                {baseSource == "1" && (
                    <TextField
                        fullWidth
                        size="medium"
                        id="domain"
                        label={language?.sources_page?.domain ?? "Domain"}
                        placeholder="Domain"
                        value={domain}
                        onChange={(event) => {
                            setDomain(event.target.value);
                            if (error.domain) {
                                setError((prev) => ({
                                    ...prev,
                                    domain: false,
                                }));
                            }
                        }}
                        error={!!error.domain}
                        helperText={error.domain ? requiredMessage : ""}
                    />
                )}

                {((isEditMode && isEditModeReady) || !isEditMode) && (
                    <Box>
                        <DynamicFieldTable
                            isScrollable={false}
                            maxHeight={null}
                            fieldsConfiguration={fieldsConfiguration}
                            fields={tableFields}
                            setFields={handleFieldsChange}
                            minLength={1}
                            showStatus={false}
                        />
                        {error.fields && (
                            <FormHelperText error sx={{ mt: 1 }}>
                                {requiredMessage}
                            </FormHelperText>
                        )}
                    </Box>
                )}
            </Stack>
        </Box>
    );

    const modalFooter = (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
            }}
        >
            <Button
                color="error"
                variant="outlined"
                className="btn-black-outlined"
                onClick={handleClose}
            >
                {language?.buttons?.close ?? "Close"}
            </Button>
            <div className="position-relative overflow-hidden z-1">
                {isLoading && (
                    <div className="circle-loader">
                        <ClipLoader color={"#000"} size={17} />
                    </div>
                )}
                <Button
                    onClick={handleClickCreateBtn}
                    className="tracker-save-btn modal-save-btn"
                    color="primary"
                    variant="contained"
                    disabled={isLoading}
                >
                    {isEditMode ? "Save" : "Create"}
                </Button>
            </div>
        </Box>
    );

    return (
        <GenericModal
            open={show}
            onClose={handleClose}
            title={isEditMode ? "Edit Source API" : "Create New Source API"}
            content={modalContent}
            footer={modalFooter}
            maxWidth="md"
        />
    );
};

export default AddEditSourceApi;
